import React, { useEffect, useState } from 'react'
import { AppProps } from 'next/app'
import { Refine, RefineProps } from '@pankod/refine-core'
import routerProvider from '@pankod/refine-nextjs-router'
import {
  notificationProvider,
  ReadyPage,
  ErrorComponent,
  Icons,
  Result,
  Button,
  ConfigProvider,
  Empty,
} from '@pankod/refine-antd'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { appWithTranslation } from 'next-i18next'
import Cookies from 'js-cookie'
import { InstUISettingsProvider } from '@instructure/emotion'
import { generateInstanceCounterMap } from '@instructure/ui-react-utils'

import {
  Title,
  Header,
  Sider,
  Footer,
  Layout,
  OffLayoutArea,
} from '@components/layout'
import Homepage from '@components/pages/Homepage'
import Login from '@components/pages/Login'
/** Start of resource list */
import DashboardLoader from '@components/pages/DashboardLoader'
import { LMSAdminManagementList } from '@resources/lms-management/admin'
import {
  LMSEntityManagementList,
  LMSEntityManagementShow,
} from '@resources/lms-management/entity'
import {
  LMSProgramManagementList,
  LMSProgramManagementShow,
} from '@resources/lms-management/program'
import {
  LMSProgramLocalManagementList,
  LMSProgramLocalManagementShow,
} from '@resources/lms-management/program-lokal'
import { LMSClassManagementShow } from '@resources/lms-management/class'
import { LMSWebinarManagementShow } from '@resources/lms-management/webinar'
import FormCreate from '@resources/form-generator/create'
import FormList from '@resources/form-generator/list'
import FormEdit from '@resources/form-generator/edit'
import { LMSProgramTagList } from '@resources/lms-management/tag'
/** End of resource list */
import dataProvider from 'src/dataProvider'
import { SESSION } from 'src/constant'
import { authProvider } from 'src/authProvider'
import { enforcer } from '../src/accessControl'
import '@wartek-id/design-tokens/dist/guru-desktop/css/color.css'
import '@wartek-id/design-tokens/dist/guru-desktop/css/font.css'
import '@wartek-id/design-tokens/dist/guru-desktop/css/size.css'
import '@wartek-id/design-tokens/dist/guru-desktop/css/elevation.css'
import 'styles/global.css'
import { UserSession } from 'types/user'
import useGetACL from 'src/hooks/useGetACL'
import { LOCAL_STORAGE_THEME_VAR, THEME } from 'src/helpers/theme'
import { FormContextProvider } from '@components/FormBuilder/formContext'
import {
  WebinarScheduleManagementList,
  WebinarEvidenceManagementList,
  WebinarClassScheduleShow,
} from '@resources/webinar-management'
import { ShowStudentsGradeReportList } from '@resources/lms-management/grade-report'
import { basicPpgManagementResource } from '@resources/basic-ppg-management/resource'
import {
  AngkatanPPGList,
  RombelList,
  RombelShow,
} from '@resources/angkatan-ppg-management'
import { lptkResource } from '@resources/basic-ppg-management/lptk-resource'
import { ErrorBoundary } from '@components/ErrorBoundary'
if (
  typeof window !== 'undefined' &&
  localStorage.getItem(LOCAL_STORAGE_THEME_VAR) === THEME.LIGHT
) {
  import('styles/theme-light.less')
} else {
  import('styles/theme-dark.less')
}
import 'styles/default.less'
import { opsToolkitManagementResource } from '@resources/ops-toolkit/resource'
import { dasusManagementResource } from '@resources/dasus-management/resource'
import {
  CertificateTemplateList,
  CertificateTemplateShow,
} from '@resources/lms-management/certificate-template'
import { LMSCertificateCategoriesManagementList } from '@resources/lms-management/certificate-categories'
import ParticipantManagementList from '@resources/lms-management/participant-management/ParticipantManagementList'
import ReportsAndFinalScore from '@resources/lms-management/reports-and-final-score/ReportsAndFinalScore'
import { diklatManagementResource } from '@resources/pendaftaran-diklat/resource'
import { LMSLandingPageList } from '@resources/lms-management/landing-page'
import { PenandatangananSertifikatList } from '@resources/tanda-tangan-elektronik/penandatanganan-sertifikat'

const LMS_API_URL = process.env.LMS_API_URL
const MICROLEARNING_API_URL = process.env.MICROLEARNING_API_URL
const V1_API_URL = process.env.V1_API_URL
const clientId = process.env.NEXT_PUBLIC_GOOGLE_ID

const reactQueryOptions: Exclude<
  RefineProps['options'],
  undefined
>['reactQuery'] = {
  clientConfig: {
    defaultOptions: {
      queries: {
        /**
         * Set default max query retry to 1 (globally),
         * lowering load time when error occured from the server.
         * Default was 3.
         */
        retry: 1,
      },
    },
  },
}

function MyApp({ Component, pageProps, router }: AppProps): JSX.Element | null {
  const [ACLData, loadACLState] = useGetACL()
  const [LMSRole, setLMSRole] = React.useState<string | null>(null)
  const userSession = Cookies.get(SESSION.USER_SESSION)

  /** Force CSR */
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null // Prevents SSR entirely

  if (loadACLState === 'idle' || loadACLState === 'loading') {
    return <DashboardLoader />
  }

  if (loadACLState === 'error') {
    return (
      <Result
        status="500"
        title="500"
        subTitle="Sorry, something went wrong. Please try again later."
        extra={
          <Button onClick={() => location.reload()} type="primary">
            Reload
          </Button>
        }
      />
    )
  }

  return (
    <InstUISettingsProvider instanceCounterMap={generateInstanceCounterMap()}>
      <GoogleOAuthProvider clientId={clientId}>
        <ConfigProvider
          renderEmpty={() => (
            <Empty
              className="!my-2"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Tidak ada data"
            />
          )}
        >
          <Refine
            routerProvider={routerProvider}
            notificationProvider={notificationProvider}
            DashboardPage={Homepage}
            ReadyPage={ReadyPage}
            catchAll={<ErrorComponent />}
            dataProvider={{
              /** Both data provider still provided for fallback */
              default: dataProvider(LMS_API_URL),
              lms: dataProvider(LMS_API_URL),
              microlearning: dataProvider(MICROLEARNING_API_URL),
              v1: dataProvider(V1_API_URL),
            }}
            authProvider={authProvider}
            LoginPage={Login}
            options={{ reactQuery: reactQueryOptions, disableTelemetry: true }}
            accessControlProvider={{
              can: async ({ resource, action, params }) => {
                if (!userSession) {
                  return Promise.reject()
                }
                const { user }: UserSession = JSON.parse(userSession)
                setLMSRole(user.LMSRole)
                /**
                 * Groups returned here has a potential bug due to maximum only show 10 groups/roles (Guru Core Login API)
                 * See more: https://wartek.atlassian.net/wiki/spaces/MOL/pages/2192343075/CMS+untuk+banding+validasi+aksi+nyata#Consequences
                 * */
                const roles = [user.LMSRole]
                  .concat(user.groups.map(({ name }) => name))
                  .filter(Boolean)

                if (action === 'field') {
                  const can = await enforcer(ACLData).then((e) =>
                    e.enforce(roles, `${resource}/${params?.field}`, action),
                  )
                  return Promise.resolve({ can })
                }

                return Promise.resolve({
                  can:
                    resource === 'dashboard' ||
                    (await enforcer(ACLData).then((e) =>
                      e.enforce(roles, resource, action),
                    )),
                })
              },
            }}
            resources={[
              {
                name: 'tanda-tangan-elektronik',
                icon: <Icons.HighlightOutlined />,
                options: {
                  label: 'Tanda Tangan Elektronik',
                  route: 'tanda-tangan-elektronik',
                },
              },
              {
                name: 'penandatanganan-sertifikat',
                parentName: 'tanda-tangan-elektronik',
                list: PenandatangananSertifikatList,
                options: {
                  label: 'Penandatanganan Sertifikat',
                  route: 'penandatanganan-sertifikat',
                },
              },
              {
                name: 'form-generator',
                icon: <Icons.FormOutlined />,
                options: {
                  label: 'LTI Form Generator',
                  route: 'form-generator',
                },
                list: FormList,
                create: FormCreate,
                edit: FormEdit,
              },
              {
                name: 'LMS-management',
                icon: <Icons.SolutionOutlined />,
                options: {
                  label: 'LMS Management',
                  route: 'lms-management',
                },
              },
              {
                name: 'lms-admin-management',
                parentName: 'LMS-management',
                list: LMSAdminManagementList,
                options: {
                  label: 'Admin',
                  route: 'lms-admin-management',
                },
                canDelete: true,
              },
              {
                name: 'lms-entity-management',
                parentName: 'LMS-management',
                list: LMSEntityManagementList,
                show: LMSEntityManagementShow,
                options: {
                  label: 'Entitas',
                  route: 'lms-entity-management',
                },
                canDelete: true,
              },
              {
                name: 'lms-program-management',
                parentName: 'LMS-management',
                list: LMSProgramManagementList,
                show: LMSProgramManagementShow,
                options: {
                  label: 'Program',
                  route: 'lms-program-management',
                  include: [
                    '/lms-program-local-management',
                    '/lms-class-management',
                    '/lms-webinar-management',
                    '/certificate-templates',
                    '/participant-management',
                    '/reports-and-final-score',
                    ...(LMSRole === 'admin-entitas' ? ['/grade-report'] : []),
                  ],
                },
                canDelete: true,
              },
              {
                name: 'lms-program-tag-management',
                parentName: 'LMS-management',
                list: LMSProgramTagList,
                options: {
                  label: 'Tag',
                  route: 'lms-program-tag-management',
                },
                canDelete: false,
              },
              {
                name: 'lms-landing-page-management',
                parentName: 'LMS-management',
                list: LMSLandingPageList,
                options: {
                  label: 'Landing Page',
                  route: 'lms-landing-page-management',
                },
                canDelete: true,
              },
              {
                name: 'lms-certificate-categories-management',
                parentName: 'LMS-management',
                list: LMSCertificateCategoriesManagementList,
                options: {
                  label: 'Kategori Sertifikat',
                  route: 'lms-certificate-categories-management',
                },
                canDelete: false,
              },
              {
                name: 'participant-management',
                parentName: 'LMS-management',
                list: ParticipantManagementList,
                options: {
                  label: 'Kelola Data Peserta',
                  route: 'participant-management',
                  hide: true,
                },
              },
              {
                name: 'reports-and-final-score',
                parentName: 'LMS-management',
                list: ReportsAndFinalScore,
                options: {
                  label: 'Laporan dan Nilai Akhir',
                  route: 'reports-and-final-score',
                  hide: true,
                },
              },
              {
                name: 'lms-program-local-management',
                parentName: 'LMS-management',
                list: LMSProgramLocalManagementList,
                show: LMSProgramLocalManagementShow,
                options: {
                  label: 'Program Lokal',
                  route: 'lms-program-local-management',
                  hide: true,
                },
                canDelete: true,
              },
              {
                name: 'certificate-templates',
                parentName: 'LMS-management',
                list: CertificateTemplateList,
                show: CertificateTemplateShow,
                options: {
                  label: 'Certificate Templates',
                  route: 'certificate-templates',
                  hide: true,
                },
                canDelete: true,
              },
              {
                name: 'lms-class-management',
                parentName: 'LMS-management',
                show: LMSClassManagementShow,
                options: {
                  label: 'Kelas',
                  route: 'lms-class-management',
                  hide: true,
                },
                canDelete: true,
              },
              {
                name: 'lms-webinar-management',
                parentName: 'LMS-management',
                show: LMSWebinarManagementShow,
                options: {
                  label: 'Webinar',
                  route: 'lms-webinar-management',
                  hide: true,
                },
                canDelete: true,
              },
              // FOR INSTRUCTOR
              {
                name: 'webinar-schedule-management',
                icon: <Icons.VideoCameraOutlined />,
                options: {
                  label: 'Daftar Webinar',
                  route: 'webinar-schedule-management',
                },
                list: WebinarScheduleManagementList,
                show: WebinarClassScheduleShow,
              },
              {
                name: 'webinar-schedule-list',
                icon: <Icons.UnorderedListOutlined />,
                options: {
                  label: 'Jadwal Webinar',
                  route: 'webinar-schedules',
                },
                list: WebinarEvidenceManagementList,
              },
              // Program Reporting
              {
                name: 'program-grades-report-list',
                parentName: 'LMS-management',
                options: {
                  label: 'Grade Report',
                  route: 'grade-report',
                  hide: LMSRole === 'admin-entitas',
                },
                list: ShowStudentsGradeReportList,
              },
              // Basic PPG Management (Batch & LPTK)
              ...basicPpgManagementResource,
              ...lptkResource,
              // Angkatan PPG Management,
              {
                name: 'angkatan-ppg-management',
                options: {
                  label: 'Angkatan',
                  route: 'angkatan-ppg',
                  include: ['/rombel-ppg'],
                },
                list: AngkatanPPGList,
              },
              {
                name: 'rombel-ppg-management',
                parentName: 'angkatan-ppg-management',
                options: {
                  label: 'Rombel',
                  route: 'rombel-ppg',
                  hide: true,
                },
                list: RombelList,
                show: RombelShow,
              },
              ...diklatManagementResource,
              ...opsToolkitManagementResource,
              ...dasusManagementResource,
            ]}
            Title={Title}
            Header={Header}
            Sider={Sider}
            Footer={Footer}
            Layout={Layout}
            OffLayoutArea={OffLayoutArea}
          >
            <ErrorBoundary resetKeys={[router.asPath]}>
              <FormContextProvider>
                <Component {...pageProps} />
              </FormContextProvider>
            </ErrorBoundary>
          </Refine>
        </ConfigProvider>
      </GoogleOAuthProvider>
    </InstUISettingsProvider>
  )
}

export default appWithTranslation(MyApp)
